import React, { useState } from 'react';
import GoBookNowLogo from './GoBookNowLogo';
import { login, isServerLoginConfigured } from '../services/authClient';

const PasswordModal = ({ onCorrectPassword }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Local-dev fallback only — used when no login endpoint is configured.
  const localPassword = 'amazon';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (isServerLoginConfigured()) {
      // Real auth: exchange the password for a short-lived token server-side.
      setLoading(true);
      try {
        await login(password);
        onCorrectPassword();
      } catch (err) {
        setError(err?.message || 'Login failed. Please try again.');
        setTimeout(() => setError(''), 4000);
      } finally {
        setLoading(false);
      }
      return;
    }

    // No login endpoint (local dev / static demo): client-side check only.
    if (password === localPassword) {
      onCorrectPassword();
    } else {
      setError('Incorrect password. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center z-50 p-4" 
         style={{
           backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("/images/manhattan-evening.png")',
           backgroundSize: 'cover',
           backgroundPosition: 'center'
         }}>
      <div className="bg-white rounded-lg shadow-2xl overflow-hidden max-w-md w-full">
        <div className="bg-linear-to-r from-blue-600 to-indigo-800 p-6">
          <h2 className="text-white text-2xl font-bold">Welcome to Go Book Now</h2>
          <p className="text-blue-100 mt-2">AI-powered travel booking demo</p>
        </div>
        
        <div className="p-6">
          <div className="flex justify-center mb-6">
            <GoBookNowLogo height="120" />
          </div>
          
          <p className="text-gray-700 mb-6 text-center">
            This demo is password protected and showcases an AI-powered travel booking interface
            built as inspiration for customers.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
                autoFocus
                disabled={loading}
              />
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md transition duration-150"
            >
              {loading ? 'Signing in…' : 'Access Demo'}
            </button>
          </form>
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t">
          <p className="text-sm text-gray-600">
            For access, please contact:
          </p>
          <div className="mt-2">
            <a 
              href="mailto:matundu@amazon.com" 
              className="text-blue-600 hover:underline text-sm flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              matundu@amazon.com
            </a>
            <a 
              href="https://www.linkedin.com/in/matiasundurraga/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline text-sm flex items-center mt-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              Matias Undurraga
            </a>
          </div>
          <p className="mt-3 text-xs text-gray-500 italic">
            This demo was built as a way to inspire customers about the possibilities of AI-powered travel booking.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordModal;